import React, { useEffect, useState } from "react";
import { useDatastore, useServerCallResult, useModuleGlobal } from "util/datastore";
import { ScrollView, View } from "react-native-web";
import { Heading, TextField, UtilityText } from 'component/text';
import { BreadCrumb, CTAButton, Tag } from 'component/button';
import { Information, OverflowMenuHorizontal, Search } from "@carbon/icons-react";
import { colorSurfacePrimary, colorSurfaceSecondary, colorTagHighlight, colorTextPrimary } from 'component/color';
import { Checkbox } from 'component/form';
import { StyleSheet, DimensionValue } from "react-native";
import { HorizBox, Pad, Separator } from 'component/basics';
import { BlocklistAddTermModal, BlocklistDeleteModal, BlocklistEditModal, BlocklistHelpModal, BlocklistItemEditDeleteMenu, BlocklistMultiSelectionDialog } from "./blocklistdialogs";
import { generateAlternativeSpellings } from "./blocklistutil";
import { Banner } from 'component/banner';
import { BlocklistTextInputWithChips } from "./blocklistTextInput";
import { useHasCapability } from 'component/admin';
import { setToastMessage } from 'open-modules/zdf/component/toast';
import { undefS, undefR, t, TLabel, computedLabel } from 'util/stdtypes';
import { useParentDatastore } from 'open-modules/zdf/component/parentdatastore';
import { moduleGlobalBlocklistLastUpdate } from 'open-modules/zdf/component/moddashboard/moddashboardtypes';
import { Popup } from 'component/popup';
import { DropDownSelector } from "component/popup";

export interface BlocklistEntry {
    key?: string;
    term: string;
    hasAlternativeSpellings: boolean;
    isSingularTerm: boolean;
}

const BlocklistStyle = StyleSheet.create({
    blocklist: {
        width: "100%",
        paddingTop: 80,
        paddingBottom: 80,
        display: "flex",
        overflow: "visible",
        flexShrink: 0,
        backgroundColor: colorSurfaceSecondary,
    },
    footerDialogContainer: {
        position: "absolute",
        width: 490,
        marginLeft: "auto",
        marginRight: "auto",
        left: 0,
        right: 0,
        bottom: "15%",
    }
});


function useBlocklist(): BlocklistEntry[] {
    const refreshTimer = useModuleGlobal(moduleGlobalBlocklistLastUpdate);
    const blocklist = useServerCallResult("blocklist", "getBlocklist", { time: refreshTimer }) as Record<string, BlocklistEntry> | null;

    if (blocklist && Object.keys(blocklist).length > 0) {
        return Object.values(blocklist).sort((a, b) => a.term.localeCompare(b.term));
    } else {
        return [];
    }
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function Blocklist(): JSX.Element {
    const s = BlocklistStyle;

    const blocklistEntries = useBlocklist();
    const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
    const [shouldShowSelectionDialog, setShouldShowSelectionDialog] = useState<boolean>(false);

    function deselectTerms({ termsToDeselect }: { termsToDeselect: string[] }) {
        let updatedSelectedTerms = selectedTerms;
        termsToDeselect.forEach((termToDeselect) => {
            updatedSelectedTerms = updatedSelectedTerms.filter((term) => term !== termToDeselect);
        });
        setSelectedTerms(updatedSelectedTerms);
    }

    useEffect(() => {
        setShouldShowSelectionDialog(selectedTerms.length > 0);
    }, [selectedTerms]);

    // TODO: How can the RestrictedScreen be used here (It is not full screen here)
    const viewAccess = useHasCapability("moderation/view-blocklist")
    const editAccess = useHasCapability("moderation/edit-blocklist");
    
    if (!viewAccess) {
        return <Banner><UtilityText label={t('You do not have access to this feature')}/></Banner>
    }

    return (
        <View style={s.blocklist}>
            <BlocklistHeader />
            {editAccess && <>
                <Pad size={24} />
                <BlocklistAddTermField />
            </>}
            <Pad size={60} />
            <BlocklistSearchField />
            <Pad size={24} />
            <BlocklistItemList
                blocklistEntries={blocklistEntries}
                selectedTerms={selectedTerms}
                setSelectedTerms={setSelectedTerms}
            />
            {shouldShowSelectionDialog === true ? (
                <View style={s.footerDialogContainer}>
                    <BlocklistMultiSelectionDialog
                        selectedTerms={shouldShowSelectionDialog ? selectedTerms : []}
                        deselectTerms={({ termsToDeselect }) => deselectTerms({ termsToDeselect })}
                    />
                </View>
            ) : null}
        </View>
    );
}

function BlocklistHeader() {
    const [shouldShowInfoModal, setShouldShowInfoModal] = useState<boolean>(false);

    return <View style={{ gap: 16 }}>
        <Heading label={t("Blocklist")} type="large" />
        <HorizBox center>
            <UtilityText
                label={t("Manage terms that should be reported automatically.")}
                type="large"
            />
            <Pad size={5} />
            <BreadCrumb icon={Information} iconProps={{color: colorTextPrimary}} onPress={() => setShouldShowInfoModal(true)} />
            {shouldShowInfoModal === true && <BlocklistHelpModal onClose={() => setShouldShowInfoModal(false)} />}
        </HorizBox>
    </View>
}

const BlocklistAddTermFieldStyle = StyleSheet.create({
    baseContainer: {
        width: "100%",
    },
    inputFieldAndButtonContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%",
    },
    inputField: {
        width: "calc(100% - 24px - 170px)" as unknown as DimensionValue,
    },
    addTermButton: {
        width: 170,
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function BlocklistAddTermField(): JSX.Element {
    const [shouldShowAddTermDialog, setShouldShowAddTermDialog] = useState<boolean>(false);
    const [terms, setTerms] = useState<string[]>([]);
    const parentDatastore = useParentDatastore();
    const [plainText, setPlainText] = useState<string>("");
    const s = BlocklistAddTermFieldStyle;
    const datastore = useDatastore();
    const [errorMessage, setErrorMessage] = useState<TLabel | "">("");

    async function inBlocklist(text: string): Promise<boolean> {
        const result = await datastore.callServerAsync("blocklist", "findTermInBlocklist", {
            term: text,
        });
        return result;
    }
    /*
    function handleTextChange(text) {
        setPlainText(text);
        if (text.trim().length > 0) {
            setErrorMessage("");  
        }
    }
        */

    async function addTerm(): Promise<void>{
        const plainTextLowerCase = plainText.toLocaleLowerCase();
        if (plainTextLowerCase.trim().length > 0 && !(await inBlocklist(plainTextLowerCase))) {
            setTerms((prevTerms) => [...new Set([...prevTerms, plainTextLowerCase.trim()])]);
            setPlainText(""); 
        }
        if (terms.length > 0 || plainTextLowerCase.trim().length > 0 && !(await inBlocklist(plainTextLowerCase))) {
            setShouldShowAddTermDialog(true);
            setPlainText("");
        } else {
            setErrorMessage(t("This term is already in the list"));
        }
    }

    function onSave(): void {
        try {
            const termsCount = terms.length;
            const singleTerm = terms[0];
            setTerms([]);
            setToastMessage({
                datastore: parentDatastore,
                label: termsCount > 1
                    ? t("{termsCount} terms were successfully added")
                    : t('The term "{singleTerm}" was added'),
                text: undefS,
                visible: true,
                type: "success",
                formatParams: { termsCount, singleTerm },
            });
        } catch (e) {
            setToastMessage({
                datastore: parentDatastore,
                label: t("Something went wrong, try again later"),
                formatParams: undefR,
                text: undefS,
                visible: true,
                type: "error",
            });
        }
    }
    
    return (
        <View style={s.baseContainer}>
            <View style={s.inputFieldAndButtonContainer}>
                <View style={s.inputField}>
                    <BlocklistTextInputWithChips terms={terms} setTerms={setTerms} inputText={plainText} setInputText={setPlainText} testID={'blocklistChipField'} errorMessage={errorMessage} setErrorMessage={setErrorMessage}/>
                </View>
                <Pad size={24} />
                <View style={s.addTermButton}>
                    <CTAButton
                        label={t("Add term")}
                        wide
                        onPress={addTerm}
                    />
                </View>
            </View>
            {shouldShowAddTermDialog && terms.length > 0 && (
                <BlocklistAddTermModal
                    terms={terms}
                    onClose={() => setShouldShowAddTermDialog(false)}
                    onSave={onSave}
                />
            )}
        </View>
    );
}


export function BlocklistSearchField(): JSX.Element {
    return (
        <View style={{ width: "33%" }}>
            <TextField value={undefS} placeholder={t("Search...")} icon={Search} multiline={false} />
        </View>
    );
}

const BlocklistItemListStyle = StyleSheet.create({
    blocklistScrollView: {
        width: "100%",
        height: 480,
        flexDirection: "column",
        gap: 8,
        overflow: "hidden" as any, // FIXME: overflowX unsupported on native
        flexShrink: 0,
    }
});

export interface BlocklistItemListProps {
    blocklistEntries: BlocklistEntry[];
    selectedTerms: string[];
    setSelectedTerms: (terms: string[]) => void;
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function BlocklistItemList({ blocklistEntries, selectedTerms, setSelectedTerms }: BlocklistItemListProps): JSX.Element {
    const sortingOrderOptions = [
        { key: "ascending", label: t("A-Z") },
        { key: "descending", label: t("Z-A") },
    ];
    let sortedEntries: BlocklistEntry[] = []

    const [sortingOrder, setSortingOrder] = useState<string>("ascending");
    const [sortedBlocklistEntries, setSortedBlocklistEntries] = useState<BlocklistEntry[]>([]);
    const characterReplacements = useServerCallResult("blocklist", "getCharacterReplacements") || {};

    const s = BlocklistItemListStyle;

    useEffect(() => {
        if (sortingOrder === "descending") {
            sortedEntries = [...blocklistEntries].sort((a, b) => b.term.localeCompare(a.term));
        } else if (sortingOrder === "ascending") {
            sortedEntries = [...blocklistEntries].sort((a, b) => a.term.localeCompare(b.term));
        }

        setSortedBlocklistEntries(
            sortedEntries.map((blocklistEntry) => {
                return { ...blocklistEntry };
            })
        );
    }, [sortingOrder, blocklistEntries]);

    // FIXME: This function is too large and should be broken up
    /* eslint-disable-next-line max-lines-per-function */
    function updateSelection({ blocklistEntry, isSelected, pressedShiftKey }: { blocklistEntry: BlocklistEntry; isSelected: boolean; pressedShiftKey: boolean }) {
        let updatedSelectedTerms = selectedTerms;

        if (!pressedShiftKey) {
            // Remove term from selection
            if (selectedTerms.includes(blocklistEntry.term) && isSelected === false) {
                updatedSelectedTerms = selectedTerms.filter((term) => term !== blocklistEntry.term);
            }
            // Add term to selection
            else if (!selectedTerms.includes(blocklistEntry.term) && isSelected === true) {
                updatedSelectedTerms = [...selectedTerms, blocklistEntry.term];
            }
        }

        // Select a range of terms by selecting an element, pressing Shift and selecting another element
        else {
            const startIndex = sortedBlocklistEntries.indexOf(blocklistEntry);
            let endIndex;

            // If there are no other selected elements
            if (selectedTerms.length === 0) {
                setSelectedTerms([blocklistEntry.term]);
            }
            // If there are other selected elements, determine the range of elements
            else {
                endIndex = sortedBlocklistEntries.findIndex(
                    (blocklistEntry) => blocklistEntry.term === selectedTerms[selectedTerms.length - 1]
                );

                const blocklistEntriesSelectionRange =
                    startIndex < endIndex
                        ? sortedBlocklistEntries.slice(startIndex, endIndex + 1)
                        : sortedBlocklistEntries.slice(endIndex, startIndex + 1);

                // Remove range from selection
                if (selectedTerms.includes(blocklistEntry.term) && isSelected === false) {
                    blocklistEntriesSelectionRange.forEach((blocklistEntry) => {
                        updatedSelectedTerms = updatedSelectedTerms.filter((term) => term !== blocklistEntry.term);
                    });
                }
                // Add range to selection
                else if (!selectedTerms.includes(blocklistEntry.term) && isSelected === true) {
                    blocklistEntriesSelectionRange.forEach((blocklistEntry) => {
                        // Check for each term if it is already in the blocklist
                        if (!updatedSelectedTerms.includes(blocklistEntry.term)) {
                            updatedSelectedTerms = [...updatedSelectedTerms, blocklistEntry.term];
                        }
                    });
                }
            }
        }

        setSelectedTerms(updatedSelectedTerms);
    }

    return (
        <View>
            <DropDownSelector
                value={sortingOrder}
                options={sortingOrderOptions}
                onChange={setSortingOrder}
            />
            <Pad />
            <ScrollView style={s.blocklistScrollView}>
                {sortedBlocklistEntries.length > 0 ? (
                    sortedBlocklistEntries.map((blocklistEntry, index) => (
                        <View key={blocklistEntry.term} testID={`item-${blocklistEntry.term}`}>
                            {index > 0 && <Separator />}
                            <BlocklistItem
                                isSelected={selectedTerms.includes(blocklistEntry.term)}
                                onChangeSelection={({ isSelected, pressedShiftKey }) =>
                                    updateSelection({ blocklistEntry, isSelected, pressedShiftKey })
                                }
                                isSingularTerm={blocklistEntry.isSingularTerm}
                                hasAlternativeSpellings={blocklistEntry.hasAlternativeSpellings}
                                term={blocklistEntry.term}
                                blocklistEntries={blocklistEntries}
                                characterReplacements={characterReplacements}
                            />
                        </View>
                    ))
                ) : (
                    <Banner color={colorSurfacePrimary}>
                        <UtilityText label={t("The blocklist is empty.")} />
                    </Banner>
                )}
            </ScrollView>
        </View>
    );
}

const BlocklistItemStyle = StyleSheet.create({
    item: {
        backgroundColor: colorSurfacePrimary,
        gap: 8,
        paddingLeft: 8,
        paddingRight: 8,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        height: 48
    },
    itemStart: {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 8,
    },
    termVariants: {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-end",
        gap: 8,
        overflow: "hidden",
    },
    termVariant: {
        overflow: "hidden"
    },
    itemEnd: {
        flexDirection: "row-reverse",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 8,
        paddingRight: 8,
        overflow: "visible",
        width: 40,
    },
});

interface BlocklistItemProps {
    isSingularTerm?: boolean;
    hasAlternativeSpellings?: boolean;
    term?: string;
    isSelected?: boolean;
    onChangeSelection: (params: { isSelected: boolean; pressedShiftKey: boolean }) => void;
    blocklistEntries: BlocklistEntry[];
    characterReplacements: Record<string, string>;
}

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function BlocklistItem({
    isSingularTerm = true,
    hasAlternativeSpellings = true,
    term = "Lorem Ipsum",
    isSelected = false,
    onChangeSelection,
    blocklistEntries,
    characterReplacements,
}: BlocklistItemProps): JSX.Element {
    const [spellingVariants, setSpellingVariants] = useState<string[]>([term]);
    const preprocessedSpellingVariants = spellingVariants.join("\n|\n").split("\n");
    const [shouldShowEditModal, setShouldShowEditModal] = useState<boolean>(false);
    const [shouldShowDeleteModal, setShouldShowDeleteModal] = useState<boolean>(false);

    const editAccess = useHasCapability("moderation/edit-blocklist")
    const viewAccess = useHasCapability("moderation/view-blocklist")

    useEffect(() => {
        if (hasAlternativeSpellings) {
            setSpellingVariants(generateAlternativeSpellings({ term, characterReplacements }));
        }
    }, [characterReplacements]);

    function onSelectBlocklistItem({ event }: { event: React.MouseEvent }) {
        onChangeSelection({ isSelected: !isSelected, pressedShiftKey: event && event.shiftKey ? event.shiftKey : false });
    }
    function handleDeletedTerm() {
        onChangeSelection({ isSelected: false, pressedShiftKey: false });
    }

    const s = BlocklistItemStyle;

    return (
        <View style={s.item}>
            <View style={s.itemStart}>
                {editAccess && <View
                    onMouseDown={(event) => {
                        onSelectBlocklistItem({ event });
                    }}
                >
                    <Checkbox testID={`checkbox-${term}`} value={isSelected} onChange={() => {}} />
                </View>}
                <View style={s.termVariants}>
                    {preprocessedSpellingVariants.map((spellingVariant, index) => {
                        return (
                            <View style={s.termVariant} key={index}>
                                <UtilityText
                                    weight={index == 0 ? "strong" : "normal"}
                                    type="large"
                                    text={spellingVariant}
                                    key={index}
                                />
                            </View>
                        );
                    })}
                    {preprocessedSpellingVariants.length > 1 && (
                        <>
                            <UtilityText type="large" weight="normal" text="|" />
                            <UtilityText type="large" weight="normal" text="..." />
                        </>
                    )}
                </View>
            </View>

            <View style={s.itemEnd}>
                {editAccess && (
                    
                    <Popup
                        popupContent={() => (
                            <BlocklistItemEditDeleteMenu
                                onEditPressed={() => setShouldShowEditModal(true)}
                                onDeletePressed={() => setShouldShowDeleteModal(true)}
                            />
                        )}
                    >
                        <View testID="three-dot-menu-edit-blocklist-term"><OverflowMenuHorizontal color={colorTextPrimary}/></View>
                    </Popup>
                    
                )}
                {isSingularTerm === true && viewAccess && (
                    <Tag emoji={"✍️"} color={colorTagHighlight} label={t("Singular Term")} />
                )}
            </View>
            {shouldShowEditModal && (
                <BlocklistEditModal
                    term={term}
                    hasAlternativeSpellings={hasAlternativeSpellings}
                    isSingularTerm={isSingularTerm}
                    onClose={() => setShouldShowEditModal(false)}
                />
            )}
            {shouldShowDeleteModal && (
                <BlocklistDeleteModal
                    termsToDelete={[term]}
                    onClose={() => setShouldShowDeleteModal(false)}
                    onTermsDeleted={() => handleDeletedTerm()}
                />
            )}
        </View>
    );
}